// The slicer key derivation, in one pure module. `derive_key` in
// `api/Sandbox/title_tokens.py` mirrors it, so a board the browser has not saved yet
// and the backend validator agree on every unkeyed slicer's key.

export const KEY_RE = /^[A-Z0-9]{1,4}$/;

const DEFAULT_LABEL_RE = /^Slicer (\d{1,4})$/;
const MAX_KEY_LENGTH = 4;

/** Trim and upper-case a typed key; "" for anything that is not a string. */
export function normalizeKey(value) {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function toSet(taken) {
  const set = new Set();
  for (const key of taken ?? []) set.add(normalizeKey(key));
  return set;
}

// A numeric key that collides takes the lowest free number.
function freeNumber(first, used) {
  if (!used.has(first)) return first;
  for (let n = 1; n <= 9999; n++) {
    const candidate = String(n);
    if (!used.has(candidate)) return candidate;
  }
  return first;
}

/**
 * The default key for a slicer label, free of every key in `taken`.
 *
 * "Slicer N" gives N. Any other label keeps its letters, the first four, upper-cased;
 * no letters gives S. A collision replaces the last character with the lowest digit
 * from 2 upward (DATE, DAT2, DAT3); a one-character key appends it (S, S2).
 */
export function deriveKey(label, taken) {
  const used = toSet(taken);
  const text = typeof label === "string" ? label.trim() : "";

  const numbered = DEFAULT_LABEL_RE.exec(text);
  if (numbered) return freeNumber(String(parseInt(numbered[1], 10)), used);

  const base = text.replace(/[^A-Za-z]/g, "").slice(0, MAX_KEY_LENGTH).toUpperCase() || "S";
  if (!used.has(base)) return base;

  for (let n = 2; n <= 9999; n++) {
    const digits = String(n);
    const keep = base.length === 1 ? 1 : Math.min(base.length - 1, MAX_KEY_LENGTH - digits.length);
    if (keep < 1) break;
    const candidate = base.slice(0, keep) + digits;
    if (candidate.length > MAX_KEY_LENGTH) break;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

/**
 * One key per slicer, in order. A valid saved key is kept (the first slicer that holds
 * it wins), and every other slicer gets a derived key that is free on the board, so a
 * saved key is never rewritten because an unkeyed neighbour derived the same letters.
 */
export function assignKeys(slicers) {
  const used = new Set();
  const kept = slicers.map((slicer) => {
    const key = normalizeKey(slicer?.key);
    if (!KEY_RE.test(key) || used.has(key)) return null;
    used.add(key);
    return key;
  });
  return kept.map((key, i) => {
    if (key != null) return key;
    const derived = deriveKey(slicers[i]?.label, used);
    used.add(derived);
    return derived;
  });
}
